import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as LinkIcon, Globe, Shirt } from 'lucide-react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import Input from '../UI/Input';
import DatePicker from '../UI/DatePicker';
import TimePicker from '../UI/TimePicker';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import seriesAPI from '../../services/seriesAPI';
import { logError } from '../../utils/errorLogger';

interface AddEventToSeriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  seriesId: string;
  onSuccess: () => void;
  mainEventDate?: string; // Main event date - sub-events can be on the same day
}

interface EventFormData {
  title: string;
  event_date: string;
  event_time: string;
  location: string;
  description?: string;
  guest_limit?: number;
  editable_until?: string;
  editing_window_hours?: number;
  location_link?: string;
  website_link?: string;
  dress_code?: string;
}

const AddEventToSeriesModal: React.FC<AddEventToSeriesModalProps> = ({
  isOpen,
  onClose,
  seriesId,
  onSuccess,
  mainEventDate
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
    getValues
  } = useForm<EventFormData>({
    defaultValues: {
      title: '',
      event_date: '',
      event_time: '',
      location: '',
      description: '',
      guest_limit: undefined,
      editable_until: '',
      editing_window_hours: undefined,
      location_link: '',
      website_link: '',
      dress_code: ''
    },
    mode: 'onChange'
  });

  const watchedDate = watch('event_date');
  const watchedTime = watch('event_time');

  // Register date and time fields for validation
  React.useEffect(() => {
    register('event_date', {
      required: 'Event date is required',
      validate: (value) => {
        if (!value) return 'Event date is required';
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          return 'Event date cannot be in the past';
        }
        // Sub-events can be on the same day as the main event, but not after
        if (mainEventDate) {
          const mainDate = new Date(mainEventDate);
          mainDate.setHours(0, 0, 0, 0);
          selectedDate.setHours(0, 0, 0, 0);
          if (selectedDate > mainDate) {
            return 'Sub-event date cannot be after the main event date';
          }
        }
        return true;
      }
    });
    register('event_time', {
      required: 'Event time is required',
      validate: (value) => {
        if (!value) return 'Event time is required';
        const currentDate = getValues('event_date');
        if (!currentDate) return true; // Date validation will handle this
        const today = new Date().toISOString().split('T')[0];
        if (currentDate === today && value) {
          const [hours, minutes] = value.split(':').map(Number);
          const now = new Date();
          const eventTime = new Date();
          eventTime.setHours(hours, minutes, 0, 0);
          if (eventTime < now) {
            return 'Event time cannot be in the past';
          }
        }
        return true;
      }
    });
  }, [register, getValues, mainEventDate]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    if (!mainEventDate) return undefined;
    // Sub-events can be on the same day as the main event, but not after
    return mainEventDate;
  };

  const getMinTime = () => {
    if (!watchedDate) return undefined;
    const today = new Date().toISOString().split('T')[0];
    if (watchedDate === today) {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    return undefined;
  };

  const onSubmit = async (data: EventFormData) => {
    if (!seriesId) {
      showNotification('Series ID is missing', 'error');
      return;
    }

    // Validate required fields
    if (!data.title || !data.event_date || !data.event_time || !data.location) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare event data matching EventInSeriesSerializer
      const eventData: any = {
        title: data.title.trim(),
        event_date: data.event_date,
        event_time: data.event_time,
        location: data.location.trim(),
        description: data.description?.trim() || '',
        guest_limit: data.guest_limit || null,
        editable_until: data.editable_until || null,
        editing_window_hours: data.editing_window_hours || null,
        location_link: data.location_link?.trim() || '',
        website_link: data.website_link?.trim() || '',
        dress_code: data.dress_code?.trim() || ''
      };

      const response = await seriesAPI.addEventToSeries(seriesId, eventData);

      if (response.success) {
        showNotification(
          response.message || 'Event added to series successfully',
          'success'
        );
        onSuccess();
      } else {
        const errorMessage = response.error?.message || response.error?.details || 'Failed to add event to series';
        showNotification(errorMessage, 'error');
      }
    } catch (error: any) {
      logError('Failed to add event to series', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error?.details ||
                          error.message || 
                          'Failed to add event to series';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Event to Series"
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Title - Required */}
        <div>
          <Input
            {...register('title', {
              required: 'Event title is required',
              minLength: {
                value: 3,
                message: 'Title must be at least 3 characters'
              },
              maxLength: {
                value: 255,
                message: 'Title must be less than 255 characters'
              }
            })}
            label="Event Title"
            placeholder="Enter event title"
            error={errors.title?.message}
            required
          />
        </div>

        {/* Date and Time Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Event Date - Required */}
          <div>
            <DatePicker
              value={watchedDate}
              onChange={(value) => {
                setValue('event_date', value, { shouldValidate: true });
                trigger('event_date');
              }}
              label="Event Date"
              minDate={getMinDate()}
              maxDate={getMaxDate()}
              error={errors.event_date?.message}
              helpText={mainEventDate ? "Sub-events can be on the same day as the main event, but not after" : undefined}
              required
            />
          </div>

          {/* Event Time - Required */}
          <div>
            <TimePicker
              value={watchedTime}
              onChange={(value) => {
                setValue('event_time', value, { shouldValidate: true });
                trigger('event_time');
              }}
              label="Event Time"
              minTime={getMinTime()}
              error={errors.event_time?.message}
              required
            />
          </div>
        </div>

        {/* Location - Required */}
        <div>
          <Input
            {...register('location', {
              required: 'Location is required',
              maxLength: {
                value: 255,
                message: 'Location must be less than 255 characters'
              }
            })}
            label="Location"
            placeholder="Enter event location"
            error={errors.location?.message}
            required
          />
        </div>

        {/* Description - Optional */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Description
          </label>
          <textarea
            {...register('description', {
              maxLength: {
                value: 1000,
                message: 'Description must be less than 1000 characters'
              }
            })}
            placeholder="Enter event description (optional)"
            rows={4}
            className={`w-full px-4 py-3 border rounded-lg shadow-sm transition-all ${
              errors.description
                ? 'border-red-500'
                : theme === 'dark'
                ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-mirsal-green focus:border-mirsal-green'
                : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-mirsal-green focus:border-mirsal-green'
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Guest Limit - Optional */}
        <div>
          <Input
            {...register('guest_limit', {
              min: {
                value: 1,
                message: 'Guest limit must be at least 1'
              },
              max: {
                value: 10000,
                message: 'Guest limit must be less than 10,000'
              },
              valueAsNumber: true
            })}
            type="number"
            label="Guest Limit"
            placeholder="Leave empty to inherit from series (optional)"
            error={errors.guest_limit?.message}
            helpText="If not specified, will inherit from the series or main event"
          />
        </div>

        {/* Communication Settings */}
        <div className={`border rounded-lg p-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
          <h4 className={`text-sm font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Communication Settings
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Response Deadline
              </label>
              <select
                {...register('editable_until')}
                className={`block w-full px-3 py-2 rounded-md border shadow-sm focus:outline-none focus:ring-mirsal-green focus:border-mirsal-green transition-all ${
                  theme === 'dark' 
                    ? 'border-gray-600 bg-gray-800 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="">No deadline</option>
                <option value="24:00:00">24h before</option>
                <option value="48:00:00">48h before</option>
                <option value="72:00:00">72h before</option>
                <option value="168:00:00">1 week before</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Event Editing Window
              </label>
              <select
                {...register('editing_window_hours', { 
                  setValueAs: (v) => v === '' ? undefined : parseInt(v)
                })}
                className={`block w-full px-3 py-2 rounded-md border shadow-sm focus:outline-none focus:ring-mirsal-green focus:border-mirsal-green transition-all ${
                  theme === 'dark' 
                    ? 'border-gray-600 bg-gray-800 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="">None (no restriction)</option>
                <option value="1">1 hour before</option>
                <option value="6">6 hours before</option>
                <option value="12">12 hours before</option>
                <option value="24">24 hours before</option>
                <option value="48">48 hours before</option>
                <option value="72">72 hours before</option>
                <option value="168">1 week (168 hours) before</option>
              </select>
              <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                How long before the event starts can you still edit event details?
              </p>
            </div>
          </div>
        </div>

        {/* Additional Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            {...register('location_link', {
              required: 'Location link is required',
              pattern: {
                value: /^https?:\/\/.+/,
                message: 'Please enter a valid URL starting with http:// or https://'
              }
            })}
            type="url"
            required
            label="Location Link"
            placeholder="e.g., https://maps.google.com/..."
            error={errors.location_link?.message}
            helpText="Google Maps (or similar) link for this venue"
            leftIcon={<LinkIcon className="h-4 w-4" />}
          />
          <Input
            {...register('website_link', {
              pattern: {
                value: /^https?:\/\/.+/,
                message: 'Please enter a valid URL starting with http:// or https://'
              }
            })}
            type="url"
            label="Website Link"
            placeholder="e.g., https://event-website.com"
            error={errors.website_link?.message}
            helpText="Link to event website"
            leftIcon={<Globe className="h-4 w-4" />}
          />
        </div>

        {/* Dress Code */}
        <div>
          <Input
            {...register('dress_code', {
              maxLength: {
                value: 255,
                message: 'Dress code must be less than 255 characters'
              }
            })}
            label="Dress Code"
            placeholder="e.g., Formal, Casual, Black Tie"
            error={errors.dress_code?.message}
            helpText="Dress code information for the event"
            leftIcon={<Shirt className="h-4 w-4" />}
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
          >
            Add Event
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddEventToSeriesModal;

